
import React, { useState } from 'react';

interface SetupViewProps {
  onSetupSuccess: (token: string) => void;
}

const SetupView: React.FC<SetupViewProps> = ({ onSetupSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match");
    if (password.length < 8) return setError("Password too short (min 8)");

    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (res.ok) {
        const data = await res.json();
        onSetupSuccess(data.token);
      } else {
        const data = await res.json();
        setError(data.error);
      }
    } catch (err) {
      setError("Connection error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-16 px-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 border border-indigo-100 dark:border-slate-800 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
        
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-bold mb-6">
            <i className="fa-solid fa-sparkles"></i>
            Welcome to Sovereignty
          </div>
          
          <h1 className="text-4xl font-black mb-4 bg-clip-text text-transparent bg-gradient-to-br from-gray-900 to-gray-500 dark:from-white dark:to-gray-400">
            Initialize YourSite
          </h1>
          <p className="text-gray-500 mb-8 max-w-md">
            Your private, personal social server is ready. Create your owner account to begin building your world.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Choose Username</label>
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 border-none outline-none"
                  placeholder="e.g. digital_nomad"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Password</label>
                  <input 
                    type="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 border-none outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Confirm</label>
                  <input 
                    type="password" 
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 border-none outline-none"
                    required
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-rose-50 text-rose-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                <i className="fa-solid fa-circle-exclamation"></i>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={isLoading}
              className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-lg shadow-xl shadow-indigo-200 dark:shadow-none transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3"
            >
              {isLoading ? <i className="fa-solid fa-spinner animate-spin"></i> : "Launch My Site"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetupView;
